import type { QuestionnaireForm, QuestionnaireFormRequest, QuestionnaireFormResponse } from '@/types/questionnaire';

interface QuestionnaireStore {
	list: {
		isLoading: boolean
		response: Array<QuestionnaireFormResponse> | null
		errorMessage: string
	},
	form: {
		data: QuestionnaireFormResponse | null
		errorMessage: string
		isLoading: boolean
	}
	manageForm: {
		data: QuestionnaireFormResponse | null
		errorMessage: string
	}
	isLoading: boolean
}

const useQuestionnaireStore = defineStore('questionnaire', {
	state: (): QuestionnaireStore => ({
		list: {
			isLoading: true, // Set state as true to show loading state on first initial
			response: null,
			errorMessage: '',
		},
		form: {
			data: null,
			errorMessage: '',
			isLoading: false,
		},
		manageForm: {
			data: null,
			errorMessage: '',
		},
		isLoading: false,
	}),

	getters: {
		questionnaireFormId: (state) => {
			if (!state.form.data) {
				return null;
			}
			return state.form.data.id;
		},

		questionnaireList: (state): Array<QuestionnaireForm> | null => {
			if (!state.list.response) {
				return null;
			}
			return transformQuestionnaireList(state.list.response);
		},

		questionnaireFormDetail: (state): QuestionnaireForm | null => {
			if (!state.form.data) {
				return null;
			}
			return transformQuestionnaireDetail(state.form.data);
		},
	},

	actions: {

		async getQuestionnaires() {
			this.list.isLoading = true;
			this.list.errorMessage = '';

			const { data, errorMessage } = await getQuestionnairesAPI();

			if (errorMessage.value) {
				this.list.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.list.response = data.value;
			}
			this.list.isLoading = false;
		},

		async getQuestionnaireById(formId: number) {
			this.form.isLoading = true;
			this.form.errorMessage = '';

			const { data, errorMessage } = await getQuestionnaireByIdAPI(formId);

			if (errorMessage.value) {
				this.form.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.form.data = data.value;
			}

			this.form.isLoading = false;
		},

		async createQuestionnaireForm(payload: QuestionnaireFormRequest) {
			this.isLoading = true;
			this.manageForm.errorMessage = '';
			this.manageForm.data = null;

			const { data, errorMessage } = await postCreateQuestionnaireForm(payload);

			if (errorMessage.value) {
				this.manageForm.errorMessage = 'Create failed. Please try again.';
			}

			if (data.value) {
				this.manageForm.data = data.value;
			}
			this.isLoading = false;
		},

		async updateQuestionnaireForm(formId:number, payload: QuestionnaireFormRequest) {
			this.isLoading = true;
			this.manageForm.errorMessage = '';
			this.manageForm.data = null;

			const { data, errorMessage } = await patchUpdateQuestionnaireForm(formId, payload);

			if (errorMessage.value) {
				this.manageForm.errorMessage = 'Unable to update at this time. Please check to see if the questionnaire is being used in another event and try again.';
			}

			if (data.value) {
				this.manageForm.data = data.value;
			}
			this.isLoading = false;
		},

		async deleteQuestionnaireById(id: number) {
			this.list.isLoading = true;
			this.list.errorMessage = '';

			const { errorMessage } = await deleteQuestionnaireByIdAPI(id);

			if (errorMessage.value) {
				this.list.errorMessage = errorMessage.value;
			}

			this.list.isLoading = false;
		},

		async archiveQuestionnaireById(id: number) {
			this.list.isLoading = true;
			this.list.errorMessage = '';

			const { errorMessage } = await patchUpdateQuestionnaireForm(id, {
				status: FORM_STATUS.ARCHIVED,
			});

			if (errorMessage.value) {
				this.list.errorMessage = errorMessage.value;
			}

			this.list.isLoading = false;
		},
	},
});

export default useQuestionnaireStore;